import React, { useEffect, useState, useRef, useCallback, ReactElement } from "react";
import WidgetCard from "./WidgetCard";
import { buildWidgetCardFilters } from "@/ts/revstack/ui-utils";
import { BsGraphUpArrow } from "react-icons/bs";
import { CommonWidgetMetrics, RevstackFilterTypes, WidgetType } from "@ts/common-types";
import { w3WidgetFormatter } from "@/ts/revstack/formatters";
import { renderWidgetStructure } from "@/ts/revstack/renderers";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

interface IRevstackSovTrendsWidgetProps {
    widgetConfig: any;
    widgetData: any;
    widgets: any;
    title: string;
    isTitleRequired?: boolean;
    isCardRequired?: boolean;
    icon?: ReactElement
}

const RevstackSovTrendsWidget: React.FC<
    IRevstackSovTrendsWidgetProps
> = ({
    widgetConfig,
    widgetData,
    widgets,
    title,
    isTitleRequired = true,
    isCardRequired = true,
    icon
}) => {

        const selectedDateRange = useSelector((state: any) => state.selectedDateRange.value);
        const chartContainerRef = useRef<HTMLDivElement>(null);
        const chartComponentRef = useRef<any>(null);
        const disableYaxisFilter = widgetConfig?.config?.disableYaxisFilter || false;
        const [yAxisFilterOptions, setYAxisFilterOptions] = useState([]);
        const [yAxisFilter, setYAxisFilter] = useState(widgetConfig?.config?.defaultYAxisFilter || null );
        const [yAxisOppositeFilter, setYAxisOppositeFilter] = useState(widgetConfig?.config?.defaultOppositeYAxisFilter || null);
        const isDateRangeRequired = widgetConfig?.config?.isDateRangeRequired ?? true;



        // Memoized setter for category to be passed to child components
        const handleSetYAxisFilter = useCallback((value: string[]) => {
            setYAxisFilterOptions(value);
        }, []);

        // Function to render the formatted date range
        const renderDateRange = () => {
            if (selectedDateRange?.dateRange?.start && selectedDateRange?.dateRange?.end) {
                return `Period: ${dayjs(selectedDateRange.dateRange.start).format('D MMM')} - ${dayjs(selectedDateRange.dateRange.end).format('D MMM YYYY')}`;
            }
            return "";
        };

        useEffect(() => {
            if (!chartContainerRef.current || !chartComponentRef.current) return;
            const observer = new window.ResizeObserver(() => {
                if (chartComponentRef.current && chartComponentRef.current.chart) {
                    chartComponentRef.current.chart.reflow();
                }
            });
            observer.observe(chartContainerRef.current);
            return () => observer.disconnect();
        }, []);

        return (
            <WidgetCard
                overwriteDefaultClass={!isCardRequired}
                className={
                    !isCardRequired
                        ? "bg-transparent border-0 rounded-0 shadow-none"
                        : ""
                }
                style={{ height: "350px" }}
            >
                {isTitleRequired && (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3"
                        >
                            <div className="d-flex gap-1 align-items-center">
                                <span
                                    className="icon-btn"
                                    style={{
                                        marginRight: 10,
                                    }}
                                >
                                    {icon}
                                </span>
                                <div className="d-flex flex-column justify-content-start">
                                    <h4 className="mb-0">
                                        {title || "Category Performance"}
                                    </h4>
                                    {isDateRangeRequired ? (
                                        <div className="text-muted fs-8 fst-italic">{renderDateRange()}</div>
                                    ) : ""}
                                </div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                {
                                    widgetConfig?.config?.isTrendsButtonRequired ? (
                                buildWidgetCardFilters({
                                    filters: [
                                        {
                                            type: RevstackFilterTypes.TRENDS,
                                            section: "",
                                            disabled: true,
                                            isComingSoon: true
                                        }
                                    ]
                                })
                                ) : ""}
                                {
                                    yAxisFilterOptions.length > 0 ? buildWidgetCardFilters({
                                        filters: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                section: "", //TODO
                                                props: {
                                                    options: yAxisFilterOptions.length ? yAxisFilterOptions : [],
                                                    defaultValue: yAxisFilter ? yAxisFilter : widgetConfig?.config?.defaultYAxisFilter,
                                                    onChange: setYAxisFilter,
                                                    width: 140
                                                },
                                            },
                                        ]}) : ""
                                }
                                {
                                    yAxisFilterOptions.length > 0 && widgetConfig?.config?.defaultOppositeYAxisFilter ? buildWidgetCardFilters({
                                        filters:[
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                section: "", //TODO
                                                props: {
                                                    options: yAxisFilterOptions.length ? yAxisFilterOptions : [],
                                                    defaultValue: yAxisOppositeFilter ? yAxisOppositeFilter : widgetConfig?.config?.defaultOppositeYAxisFilter,
                                                    onChange: setYAxisOppositeFilter,
                                                    width: 140
                                                },
                                            },
                                        ]
                                    }) : ""
                                }

                            </div>


                        </div>

                    </div>

                )}
                <div ref={chartContainerRef} className="w-100 h-100">
                    {renderWidgetStructure(widgets, (widgetList: any, widgetIdx: number) => {
                        const shouldShowWidget = widgetList.type === WidgetType.W3;

                        if (!shouldShowWidget) return null;

                        const { componentData } = w3WidgetFormatter(widgetConfig, widgetList, widgetData);

                        if (!widgetList.widgetComponent) {
                            console.warn(
                                `Widget component not found for type: ${widgetList.type}`
                            );
                            return null;
                        }

                        const WidgetComponent = widgetList.widgetComponent;

                        return (
                            <WidgetComponent
                                widget={widgetList}
                                key={`${widgetList.type}-${widgetIdx}`}
                                chartComponentRef={chartComponentRef}
                                widgetData={componentData}
                                graphAttributes={{
                                    yAxisTitle: componentData?.yAxisTitle,
                                    hideXAxisTitle: true,
                                }}
                                yAxisUnit={componentData?.yAxisUnit}
                                graphColors={componentData?.graphColors}
                                isLoading={componentData?.isLoading}
                                handleSetYAxisFilter={disableYaxisFilter ? null : handleSetYAxisFilter}
                                yAxisFilter={yAxisFilter}
                                yAxisOppositeFilter={yAxisOppositeFilter}
                                icon={WidgetComponent.icon}
                                primaryDimension={widgetList.queryMetaInfo?.primaryDimension}
                            />
                        );
                    })}
                </div>
            </WidgetCard>
        );
    };

export default RevstackSovTrendsWidget;
